<template>
  <div class="sharing-table">
    <p v-if="error" class="error">{{ error.message || error.description || error }}</p>
    <toolbar
      class="new-globals"
      title="Groups and Students"
      :has-search="false"
      :has-menu="false"
      @onAutocompleteSelect="addPrincipal"
    >
      <template slot="search">
        <md-menu :md-offset-y="0.1">
          <div class="search">
            <md-icon>search</md-icon>
            <md-field
              class="md-autocomplete md-focused md-active"
              :style="{background: 'white'}"
              md-menu-trigger
            >
              <md-input v-model="state.searchTerm" />
            </md-field>
          </div>
          <md-menu-content>
            <md-menu-item
              v-for="principal in filteredUsersAndGroupsOptions"
              :key="principal.id"
              @click="addPrincipal(principal)"
            >
              <md-highlight-text :md-term="state.searchTerm">{{ principal.label }}</md-highlight-text>
              <md-button class="md-icon-button md-dense md-raised md-primary">
                <md-icon>add</md-icon>
              </md-button>
            </md-menu-item>
          </md-menu-content>
        </md-menu>
      </template>
    </toolbar>
    <md-progress-bar v-if="loading" md-mode="indeterminate" />
    <md-table
      class="users-table"
      :value="principalsSorted"
      md-card
      :md-sort.sync="state.sortField"
      :md-sort-order.sync="state.sortOrder"
    >
      <md-table-row
        slot="md-table-row"
        slot-scope="{ item: principal }"
      >
        <md-table-cell width="50px" class="remove-principal">
          <md-checkbox
            :value="!!principal"
            @change="value => removePrincipal(principal, value)"
          />
        </md-table-cell>
        <md-table-cell md-label="Student/Group Name" md-sort-by="name">
          <span>{{ principalName(principal) }}</span>
        </md-table-cell>
        <md-table-cell md-label="Type" md-sort-by="role">
          <span>{{ principalRole(principal) }}</span>
        </md-table-cell>
      </md-table-row>
    </md-table>
  </div>
</template>

<script>
import map from 'lodash/map';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import toLower from 'lodash/toLower';
import last from 'lodash/last';

import Toolbar from '~lib/common/components/Toolbar';

export default {
  name: 'assignment-sharing-table',
  components: {
    Toolbar,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Array,
      default: () => [],
    },
    groups: {
      type: Array,
      default: () => [],
    },
    assignment: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    error: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      state: {
        updatedStudents: null,
        searchTerm: '',
        principalListOpen: false,
        sortField: 'name',
        sortOrder: 'asc',
      },
      roleLabels: {
        ADMIN: 'Admin',
        SUPER_ADMIN: 'Super Administrator',
        USER: 'Instructor',
        STUDENT: 'Student',
      },
    };
  },
  computed: {
    usersById() {
      return keyBy(this.users, 'user_id');
    },
    groupsById() {
      return keyBy(this.groups, '_id');
    },
    filteredUsersAndGroupsOptions() {
      if (!this.state.searchTerm) {
        return this.usersAndGroupsOptions;
      }
      return this.usersAndGroupsOptions.filter(
        u =>
          u.label &&
          u.label.toLowerCase().includes(this.state.searchTerm.toLowerCase()),
      );
    },
    usersAndGroupsOptions() {
      return [
        ...this.groups.map(group => ({
          type: 'group',
          id: group._id,
          label: this.principalName({ name: group.name, type: 'group' }),
        })),
        ...this.users.map(user => ({
          type: 'user',
          id: user.user_id,
          label:
            `${user.given_name || ''} ${user.family_name || ''}`.trim() ||
            user.email,
        })),
      ].filter(principal => !this.principalsById[principal.id]);
    },
    principalsById() {
      return keyBy(this.principals, 'id');
    },
    principals() {
      const { updatedStudents: students } = this.state;
      if (!students) {
        return [];
      }
      const principals = [
        ...map(get(students, 'groups'), groupId => ({
          type: 'group',
          id: groupId,
        })),
        ...map(get(students, 'users'), userId => ({
          type: 'user',
          id: userId,
        })),
      ];
      
      const principalsWithData = map(
        principals,
        principal => {
          if (principal.type === 'group') {
            const group = this.groupsById[principal.id];
            return group ? { ...group, ...principal } : null;
          } else if (principal.type === 'user') {
            const user = this.usersById[principal.id];
            return user ? { ...user, ...principal } : null;
          }
          return null;
        },
      );
      const nonNullPrincipals = filter(principalsWithData, p => !!p);
      return nonNullPrincipals;
    },
    principalsSorted() {
      const { sortField, sortOrder } = this.state;
      const sorted = sortBy(this.principals, principal => {
        if (sortField === 'name') {
          return toLower(this.principalName(principal));
        }
        if (sortField === 'role') {
          return this.principalRole(principal);
        }
        return 0;
      });
      if (sortOrder === 'desc') {
        return reverse(sorted);
      }
      return sorted;
    },
  },
  watch: {
    assignment(assignment) {
      if (!assignment) {
        return;
      }
      if (!this.state.assignment || assignment.id !== this.state.assignment.id) {
        this.state.updatedStudents = cloneDeep(assignment.students);
      }
    },
  },
  created() {
    this.state.updatedStudents = this.assignment
      ? cloneDeep(this.assignment.students)
      : {groups:{},users:{}};
  },
  methods: {
    principalName(principal) {
      if (principal.type === 'user') {
        return (
          `${principal.given_name || ''} ${principal.family_name ||
            ''}`.trim() || principal.email
        );
      } else if (principal.type === 'group') {
        return last(principal.name.split('::'));
      }
      return principal.name;
    },
    principalRole(principal) {
      if (principal.type === 'user') {
        return (
          this.roleLabels[
            principal.app_metadata && principal.app_metadata.role
          ] || 'Instructor'
        );
      }
      return 'Group';
    },
    addPrincipal(principal) {
      const { updatedStudents } = this.state;
      const principalType = `${principal.type}s`;
      updatedStudents[principalType] = updatedStudents[principalType] || [];
      updatedStudents[principalType] = uniq(
        updatedStudents[principalType].concat(principal.id)
      );
      this.update();
    },
    removePrincipal(principal, value) {
      if (value) return;
      const { updatedStudents } = this.state;
      const principalType = `${principal.type}s`;
      updatedStudents[principalType] = updatedStudents[principalType] || [];
      updatedStudents[principalType] = without(
        updatedStudents[principalType],
        principal.id,
      );
      this.update();
    },
    update() {
        this.$emit('update', this.state.updatedStudents);
    }
  },
};
</script>

<style scoped>
.sharing-table {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.remove-principal >>> .md-table-cell-container {
  padding-right: 0;
}

.error {
  color: red;
}

.md-table {
  flex: 1;
}

.md-button.finalize-sharing {
  margin-right: 0;
  align-self: flex-end;
}

.bottom-toolbar {
  display: flex;
  justify-content: flex-end;
}

.autocomplete-result {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>

<style>
.permission-select-option[disabled] button {
  text-decoration: line-through !important;
}
</style>